import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select.js";
import useStore from "../../../components/WorkFlow/store";
import {
  useEventEmitterContext,
  EventEmitterType,
  AgentSaveType,
} from "../../../context/event-emitter.js";
import useNodesInteractions from "../../../components/WorkFlow/hooks/use-nodes-interactions.js";
import { debounce } from "../../../lib/debounce.js";
import {
  AGENT_VOICE_OPTIONS as FALLBACK_VOICES,
  formatAgentVoiceLabel,
  resolveAgentVoice,
  voiceSupportsLanguage,
  inferVoiceProvider,
  VOICE_PROVIDER,
  CARTESIA_DEFAULT_VOICE_ID,
} from "../../../constants/agentVoices.js";

const TONE_STYLES = [
  "friendly and casual",
  "formal customer service",
  "warm and conversational",
  "gentle and reassuring",
  "confident sales agent",
  "neutral and informative",
  "nurturing nurse assistant",
  "laid-back and humorous",
  "instructional teacher",
];

const MODEL_OPTIONS = [
  { value: "gpt-4o-mini", label: "GPT-4o Mini (Default)" },
  { value: "gpt-4o", label: "GPT-4o (Advanced)" },
  { value: "claude-3-5-sonnet", label: "Claude 3.5 Sonnet" },
  { value: "claude-3-opus", label: "Claude 3 Opus" },
  { value: "llama-3-70b", label: "Llama 3 70B" },
  { value: "llama-2-70b", label: "Llama 2 70B" },
];

const LANGUAGES = {
  "en": "English (US - Standard)",
  "en-GB": "English (British)",
  "en-AU": "English (Australian)",
  "en-IN": "English (Indian)",
  "en-SG": "English (Singapore)",
  "es": "Spanish (Spain)",
  "es-MX": "Spanish (Mexico - Warm Tone)",
  "es-AR": "Spanish (Argentina)",
  "fr": "French (France)",
  "fr-CA": "French (Canadian)",
  "de": "German (Standard)",
  "de-AT": "German (Austrian)",
  "it": "Italian (Standard)",
  "pt": "Portuguese (Brazil)",
  "pt-PT": "Portuguese (Portugal)",
  "ja": "Japanese (Standard)",
  "zh": "Chinese (Mandarin - Simplified)",
  "zh-TW": "Chinese (Traditional)",
  "ko": "Korean (Standard)",
  "ru": "Russian (Standard)",
  "ar": "Arabic (Modern Standard)",
  "ar-EG": "Arabic (Egyptian)",
  "hi": "Hindi (Standard)",
  "nl": "Dutch (Standard)",
  "pl": "Polish (Standard)",
  "tr": "Turkish (Standard)",
  "sv": "Swedish (Standard)",
  "no": "Norwegian (Standard)",
  "da": "Danish (Standard)",
};

const Setting = ({ onUpdateScript, saving }) => {
  const agentSetting = useStore((state) => state.agentSetting);
  const setAgent = useStore((state) => state.setAgent);
  const setNodes = useStore((state) => state.setNodes);
  const setEdges = useStore((state) => state.setEdges);
  const { eventEmitter } = useEventEmitterContext();
  const { handleLayout } = useNodesInteractions();
  const fileInputRef = useRef(null);

  const [voices, setVoices] = useState(FALLBACK_VOICES);
  const [collapsed, setCollapsed] = useState(false);

  // Import/Export functions
  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = JSON.parse(e.target.result);

          // Validate structure
          if (!Array.isArray(data.nodes) || !Array.isArray(data.edges) || !data.agent) {
            throw new Error("Invalid flow data: missing nodes, edges, or agent");
          }

          // Load the data
          setNodes(data.nodes);
          setEdges(data.edges);
          setAgent(data.agent);

          alert(`✓ Successfully imported: ${file.name}`);
          console.log("Imported flow data:", data);
        } catch (error) {
          alert(`✗ Import failed: ${error.message}`);
          console.error("Import error:", error);
        }
      };
      reader.onerror = () => alert("Failed to read file");
      reader.readAsText(file);
    } catch (error) {
      alert(`✗ Error: ${error.message}`);
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleExport = () => {
    try {
      // Get nodes and edges from store
      const state = useStore.getState();
      const filename = `${(agentSetting.title || "workflow").replace(/\s+/g, "-").toLowerCase()}_${new Date().toISOString().slice(0, 10)}.json`;

      const flowData = {
        nodes: state.nodes,
        edges: state.edges,
        agent: agentSetting,
      };

      const jsonString = JSON.stringify(flowData, null, 2);
      const blob = new Blob([jsonString], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      alert(`✓ Exported: ${filename}`);
    } catch (error) {
      alert(`✗ Export failed: ${error.message}`);
      console.error("Export error:", error);
    }
  };

  useEffect(() => {
    const fetchVoices = async () => {
      try {
        const twilioApiUrl = process.env.REACT_APP_TWILIO_API_URL || (process.env.REACT_APP_AGENT_API_URL?.replace(':3016', ':3017') || '');
        const res = await fetch(`${twilioApiUrl}/api/voices`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (
          data?.success &&
          Array.isArray(data.voices) &&
          data.voices.length > 0
        ) {
          setVoices(data.voices.map((v) => resolveAgentVoice(v)));
        }
      } catch (err) {
        console.warn(
          "⚠️ Voices API not available, using fallback voices:",
          err
        );
        console.log('📌 Using FALLBACK_VOICES with language tags:', FALLBACK_VOICES.slice(0, 3));
        setVoices(FALLBACK_VOICES);
      }
    };

    fetchVoices();
  }, []);

  useEffect(() => {
    eventEmitter?.emit({
      type: EventEmitterType.AgentSave,
      payload: AgentSaveType.AgentSetting,
    });
  }, [agentSetting, eventEmitter]);

  // Kokoro cannot speak other languages — swap to Cartesia when the script language isn't English.
  useEffect(() => {
    const base = String(agentSetting.language || "en").split("-")[0].toLowerCase();
    if (base !== "en" && inferVoiceProvider(agentSetting.voice) === VOICE_PROVIDER.KOKORO) {
      setAgent({ voice: CARTESIA_DEFAULT_VOICE_ID });
    }
  }, [agentSetting.language, agentSetting.voice, setAgent]);

  // Download JSON
  const handleDownload = () => {
    const jsonString = JSON.stringify({ agent: agentSetting }, null, 2);
    const blob = new Blob([jsonString], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "workflow-settings.json";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const debouncedDownload = useMemo(
    () => debounce(handleDownload, 500),
    [agentSetting]
  );

  return (
    <div
      className={`transition-all duration-300 bg-white shadow-lg border-l ${collapsed ? "w-[40px]" : "w-[400px]"
        } h-[calc(100vh-64px)] flex flex-col`}
    >
      {/* Toggle Button */}
      <div className="flex justify-end p-2 border-b">
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="bg-cyan-400 hover:bg-blue-700 text-white rounded px-2 py-1 text-xs"
        >
          {collapsed ? "→" : "←"}
        </button>
      </div>

      {/* Content - Scrollable */}
      {!collapsed && (
        <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-4 pb-40">
          <h1 className="text-lg font-semibold">Agent Settings</h1>

          {/* Voice Selector */}
          <div>
            <label className="block text-sm font-medium mb-1">Voice</label>
            <Select
              value={agentSetting.voice || ""}
              onValueChange={(value) => setAgent({ voice: value })}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a voice" />
              </SelectTrigger>
              <SelectContent className="bg-white shadow-lg rounded-md max-h-60">
                {voices
                  .filter((item) => voiceSupportsLanguage(item, agentSetting.language || "en"))
                  .map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {formatAgentVoiceLabel(item)}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            {(agentSetting.language || "en").split("-")[0] !== "en" && (
              <p className="text-xs text-cyan-700 mt-1">
                Non-English calls use Cartesia TTS. Kokoro stays English-only.
              </p>
            )}
          </div>

          {/* Tone Style */}
          <div>
            <label className="block text-sm font-medium mb-1">Tone Style</label>
            <Select
              value={agentSetting.toneStyle || ""}
              onValueChange={(value) => setAgent({ toneStyle: value })}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select tone style" />
              </SelectTrigger>
              <SelectContent className="bg-white shadow-lg rounded-md">
                {TONE_STYLES.map((style) => (
                  <SelectItem key={style} value={style}>
                    {style}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Response Density */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Response Density
            </label>
            <Select
              value={agentSetting.responseDensity || ""}
              onValueChange={(value) => setAgent({ responseDensity: value })}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select density" />
              </SelectTrigger>
              <SelectContent className="bg-white shadow-lg rounded-md">
                <SelectItem value="brief">Brief</SelectItem>
                <SelectItem value="balanced">Balanced</SelectItem>
                <SelectItem value="detailed">Detailed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Empathy Level */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Empathy Level
            </label>
            <Select
              value={agentSetting.empathyLevel || ""}
              onValueChange={(value) => setAgent({ empathyLevel: value })}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select empathy level" />
              </SelectTrigger>
              <SelectContent className="bg-white shadow-lg rounded-md">
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* AI Model Selection */}
          <div className="border-t pt-3 mt-2">
            <label className="block text-sm font-medium mb-1">🤖 AI Model</label>
            <Select
              value={agentSetting.aiModel || "gpt-4o-mini"}
              onValueChange={(value) => setAgent({ aiModel: value })}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select model" />
              </SelectTrigger>
              <SelectContent className="bg-white shadow-lg rounded-md">
                {MODEL_OPTIONS.map((model) => (
                  <SelectItem key={model.value} value={model.value}>
                    {model.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Language Selection */}
          <div>
            <label className="block text-sm font-medium mb-1">🌍 Language</label>
            <Select
              value={agentSetting.language || "en"}
              onValueChange={(value) => {
                const next = { language: value };
                const base = String(value || "en").split("-")[0].toLowerCase();
                if (base !== "en" && inferVoiceProvider(agentSetting.voice) === VOICE_PROVIDER.KOKORO) {
                  next.voice = CARTESIA_DEFAULT_VOICE_ID;
                }
                setAgent(next);
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select language" />
              </SelectTrigger>
              <SelectContent className="bg-white shadow-lg rounded-md max-h-60">
                {Object.entries(LANGUAGES).map(([code, name]) => (
                  <SelectItem key={code} value={code}>
                    {name} ({code.toUpperCase()})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Temperature */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Temperature: <span className="font-bold">{(agentSetting.temperature ?? 0.5).toFixed(2)}</span>
            </label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={agentSetting.temperature ?? 0.5}
              onChange={(e) => setAgent({ temperature: parseFloat(e.target.value) })}
              className="w-full h-2 bg-blue-300 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>Focused</span>
              <span>Creative</span>
            </div>
          </div>

          {/* Max Tokens */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Max Tokens: <span className="font-bold">{agentSetting.maxTokens ?? 500}</span>
            </label>
            <input
              type="range"
              min="50"
              max="4000"
              step="50"
              value={agentSetting.maxTokens ?? 500}
              onChange={(e) => setAgent({ maxTokens: parseInt(e.target.value) })}
              className="w-full h-2 bg-blue-300 rounded-lg appearance-none cursor-pointer"
            />
            <p className="text-xs text-gray-500 mt-1">Max response length</p>
          </div>

          {/* Voice Settings */}
          <div className="border-t pt-3 mt-2">
            <label className="block text-sm font-medium mb-2">🎙️ Voice Customization</label>

            <div className="space-y-2">
              {/* Stability */}
              <div>
                <label className="text-xs font-medium">
                  Stability: {(agentSetting.voiceSettings?.stability ?? 0.75).toFixed(2)}
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={agentSetting.voiceSettings?.stability ?? 0.75}
                  onChange={(e) => setAgent({
                    voiceSettings: {
                      ...agentSetting.voiceSettings,
                      stability: parseFloat(e.target.value)
                    }
                  })}
                  className="w-full h-2 bg-purple-300 rounded-lg appearance-none cursor-pointer"
                />
              </div>

              {/* Similarity Boost */}
              <div>
                <label className="text-xs font-medium">
                  Similarity: {(agentSetting.voiceSettings?.similarityBoost ?? 0.9).toFixed(2)}
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={agentSetting.voiceSettings?.similarityBoost ?? 0.9}
                  onChange={(e) => setAgent({
                    voiceSettings: {
                      ...agentSetting.voiceSettings,
                      similarityBoost: parseFloat(e.target.value)
                    }
                  })}
                  className="w-full h-2 bg-purple-300 rounded-lg appearance-none cursor-pointer"
                />
              </div>

              {/* Speaker Boost */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={agentSetting.voiceSettings?.speakerBoost !== false}
                  onChange={(e) => setAgent({
                    voiceSettings: {
                      ...agentSetting.voiceSettings,
                      speakerBoost: e.target.checked
                    }
                  })}
                  className="w-4 h-4 rounded"
                />
                <label className="text-xs font-medium">Speaker Boost</label>
              </div>
            </div>
          </div>

          {/* Avoid Topics */}
          <div>
            <label className="block text-sm font-medium mb-1">Avoid Topics</label>
            <textarea
              className="w-full border rounded p-2 text-sm"
              rows={2}
              placeholder="Comma-separated list"
              value={agentSetting.avoidTopics || ""}
              onChange={(e) => setAgent({ avoidTopics: e.target.value })}
            />
          </div>

          {/* Formatting Preferences */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Formatting Preferences
            </label>
            <textarea
              className="w-full border rounded p-2 text-sm"
              rows={2}
              placeholder="Comma-separated list"
              value={agentSetting.formatting || ""}
              onChange={(e) => setAgent({ formatting: e.target.value })}
            />
          </div>
        </div>
      )}

      {/* Footer Buttons (sticky at bottom) */}
      {!collapsed && (
        <div className="sticky bottom-0 bg-white px-4 py-3 space-y-1 border-t shadow-md">
          <button
            onClick={handleLayout}
            className="bg-cyan-400 hover:bg-blue-700 text-white text-xs font-semibold px-3 py-1.5 rounded-md w-full"
          >
            🔄 Organize Nodes
          </button>
          <button
            onClick={onUpdateScript}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-3 py-1.5 rounded-md w-full disabled:opacity-50"
          >
            {saving ? "Updating..." : "Update Script"}
          </button>

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            onChange={handleFileSelect}
            style={{ display: "none" }}
          />

          {/* Import/Export Buttons */}
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="bg-green-600 hover:bg-green-700 text-white text-xs font-semibold px-3 py-1.5 rounded-md w-full"
          >
            📥 Import JSON
          </button>
          <button
            type="button"
            onClick={() => {
              const state = useStore.getState();
              const filename = `workflow_${new Date().toISOString().slice(0, 10)}.json`;
              const flowData = { nodes: state.nodes, edges: state.edges, agent: state.agentSetting };
              const jsonString = JSON.stringify(flowData, null, 2);
              const blob = new Blob([jsonString], { type: "application/json" });
              const url = URL.createObjectURL(blob);
              const link = document.createElement("a");
              link.href = url;
              link.download = filename;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);
            }}
            className="bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold px-3 py-1.5 rounded-md w-full"
          >
            📤 Export JSON
          </button>
        </div>
      )}
    </div>
  );
};

export default Setting;
